import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ThemeToggle from '@/components/atoms/ThemeToggle';
import ChangeLanguage from '@/components/molecules/ChangeLanguage/ChangeLanguage';
import ClientNav from '@/components/molecules/ClientNav/ClientNav';
import OptoBreadCrumb from '@/components/molecules/OptoBreadCrumb/OptoBreadCrumb';
import QuickSearchTrigger from '@/components/molecules/QuickSearchTrigger';
import QuickSearch, { useQuickSearchShortcut } from '@/components/organisms/QuickSearch';
import { useAuth } from '@/contexts/auth';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import { LayoutProvider, useLayout } from '@/contexts/layout';
import { useLogout } from '@/domain/auth';
import { useRouteInfo } from '@/hooks/useRouteInfo';
import { applyDemoMaskToClient } from '@/utils/demoMasking';

import AppHeader from '../AppHeader/AppHeader';
import AppLayout from '../AppLayout/AppLayout';
import AppSidebar from '../AppSidebar/AppSidebar';

import { useDocumentTitle } from './useDocumentTitle';

import styles from './AppShell.module.scss';

export interface AppShellProps {
    children: ReactNode;
    topBanner?: ReactNode;
    languageBar?: boolean;
    showHomeButton?: boolean;
}

export default function AppShell(props: AppShellProps) {
    const { children, topBanner, languageBar = true, showHomeButton = true } = props;
    return (
        <LayoutProvider>
            <AppShellInner
                topBanner={topBanner}
                languageBar={languageBar}
                showHomeButton={showHomeButton}>
                {children}
            </AppShellInner>
        </LayoutProvider>
    );
}

function AppShellInner({ children, topBanner, languageBar, showHomeButton }: AppShellProps) {
    const { state } = useLayout();
    const { isMobile } = state;

    useDocumentTitle();

    const { user, clients } = useAuth();
    const { isDemoMode } = useDemoMode();
    const onLogout = useLogout();
    const { clientId, locationId, cageId, pagePath } = useRouteInfo();
    const { t } = useTranslation();
    const { enabled: isQuickSearchEnabled, isOpen, close, open } = useQuickSearchShortcut(!!user);

    const internalPage = location.pathname.startsWith('/internal');

    // Process clients for display (mask when demo mode is enabled and NOT on internal pages)
    const displayClients = useMemo(() => {
        if (!isDemoMode || internalPage) {
            return clients;
        }
        return clients.map((client) => applyDemoMaskToClient(client, t));
    }, [clients, isDemoMode, internalPage, t]);

    const client = displayClients.find((c) => c.id === clientId);

    const breadcrumbs = user ? (
        <OptoBreadCrumb
            client={client}
            clients={displayClients}
            locationId={locationId}
            cageId={cageId}
            pagePath={pagePath}
            showHomeButton={showHomeButton}
        />
    ) : undefined;

    const headerUserControls =
        user && !isMobile ? (
            <div className={styles.userControls}>
                {isQuickSearchEnabled && (
                    <QuickSearchTrigger onClick={() => open('header')} variant="header" />
                )}
                <ThemeToggle variant="header" />
                {languageBar && <ChangeLanguage variant="header" />}
                <ClientNav user={user} variant="header" onLogout={onLogout} />
            </div>
        ) : undefined;

    const mobileUserControls =
        user && isMobile ? (
            <div className={`${styles.userControls} ${styles.mobileUserControls}`}>
                {isQuickSearchEnabled && (
                    <QuickSearchTrigger onClick={() => open('mobile')} variant="mobile" />
                )}
                <ThemeToggle variant="mobile" />
                {languageBar && <ChangeLanguage variant="mobile" />}
                <ClientNav user={user} variant="mobile" onLogout={onLogout} />
            </div>
        ) : undefined;

    return (
        <>
            <AppLayout
                sidebar={<AppSidebar mobileFooter={mobileUserControls} />}
                header={<AppHeader left={breadcrumbs} right={headerUserControls} />}
                topBanner={topBanner}>
                {children}
            </AppLayout>
            {isQuickSearchEnabled && <QuickSearch isOpen={isOpen} onClose={close} />}
        </>
    );
}
